import { useCallback, useEffect, useRef, useState } from 'react'
import { useChatStore } from '../state/chatStore'
import { Avatar, stateLabel } from './Avatar'
import { ThemeToggle } from './ThemeToggle'
import { MessageBubble } from './MessageBubble'
import { ListeningBubble } from './ListeningBubble'
import { SuggestionChips } from './SuggestionChips'
import { VoiceModeOverlay } from './VoiceModeOverlay'
import { useVoiceSession } from '../hooks/useVoiceSession'
import { CameraView } from './CameraView'
import { analyzeImage, framesFromFile } from '../lib/imageAnalysis'
import { useT, type UiStrings } from '../lib/i18n'

/**
 * Openers for an empty chat, so the first question is one tap away. Der erste
 * startet den geführten Rundgang (Station für Station, mit Wegbeschreibung aus
 * dem Admin) statt einer Frage.
 */
function starters(t: UiStrings) {
  return [
    { label: 'Tour', question: t.chipTour },
    { label: 'Lab', question: t.starterLab },
    { label: 'J4F', question: t.starterJ4f },
  ]
}

export function ChatView() {
  const messages = useChatStore((s) => s.messages)
  const avatarState = useChatStore((s) => s.avatarState)
  const micLevel = useChatStore((s) => s.micLevel)
  const suggestions = useChatStore((s) => s.suggestions)
  const errorMessage = useChatStore((s) => s.errorMessage)
  const sendMessage = useChatStore((s) => s.sendMessage)
  const pickSuggestion = useChatStore((s) => s.pickSuggestion)
  const loadCatalog = useChatStore((s) => s.loadCatalog)
  const cameraConsent = useChatStore((s) => s.consent?.camera ?? false)
  const micOpen = useChatStore((s) => s.micOpen)
  const micConsent = useChatStore((s) => s.consent?.microphone ?? false)
  const voiceAutoStart = useChatStore((s) => s.voiceAutoStart)
  const sitzungsHinweis = useChatStore((s) => s.sitzungsHinweis)
  const hinweisSchliessen = useChatStore((s) => s.hinweisSchliessen)
  const neuesGespraech = useChatStore((s) => s.neuesGespraech)
  const locale = useChatStore((s) => s.locale)

  /**
   * Der Sprachmodus: ein eigener Bildschirm, einmal starten und reden
   * (VoiceModeOverlay, useVoiceSession im Modus `gespraech`). Der Einstieg
   * sitzt in der Eingabeleiste — leer zeigt sie den Sprachknopf, mit Text den
   * Sende-Pfeil, wie bei ChatGPT.
   */
  const voice = useVoiceSession('gespraech')
  const { start: voiceStart, stop: voiceStop } = voice
  const [voiceOpen, setVoiceOpen] = useState(false)
  const startVoice = useCallback(() => {
    setVoiceOpen(true)
    void voiceStart()
  }, [voiceStart])
  const endVoice = useCallback(() => {
    voiceStop()
    setVoiceOpen(false)
  }, [voiceStop])

  // Einstieg per NFC-Tag mit „Vorlesen": Der Sprachmodus startet von selbst.
  // Ohne Mikrofon-Freigabe geht das nicht — dann erklärt K4ia im Chat.
  useEffect(() => {
    if (!voiceAutoStart || voice.status !== 'idle') return
    useChatStore.getState().setVoiceAutoStart(false)
    if (micConsent) startVoice()
    else void useChatStore.getState().flushVoiceEntry('chat')
  }, [voiceAutoStart, voice.status, micConsent, startVoice])

  // Bricht die Verbindung von der Gegenseite ab, geht der Bildschirm mit zu —
  // sonst stünde dort „Verbinde …“ für immer.
  const warAktivRef = useRef(false)
  useEffect(() => {
    if (voice.status === 'active') warAktivRef.current = true
    if (voice.status === 'idle' && warAktivRef.current && voiceOpen) {
      warAktivRef.current = false
      setVoiceOpen(false)
    }
    if (!voiceOpen) warAktivRef.current = false
  }, [voice.status, voiceOpen])

  const [draft, setDraft] = useState('')
  const [cameraOpen, setCameraOpen] = useState(false)
  /** Ein hochgeladenes Foto wird gerade ausgewertet. */
  const [fotoLaeuft, setFotoLaeuft] = useState(false)
  const fotoInputRef = useRef<HTMLInputElement>(null)

  /**
   * Foto statt Live-Kamera: Der Besucher macht ein Bild (oder wählt eins) und
   * K4ia wertet es aus — Showcase-Erkennung, sonst Beschreibung; im
   * Sprachmodus gesprochen. Die Live-Erkennung verlangt, das Handy ruhig auf
   * den Stand zu halten; ein Foto nicht.
   */
  async function handleFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setFotoLaeuft(true)
    try {
      await analyzeImage(await framesFromFile(file))
    } finally {
      setFotoLaeuft(false)
    }
  }
  const listRef = useRef<HTMLDivElement>(null)
  const isBusy = avatarState === 'thinking'
  const t = useT()

  // Showcases (mit FAQ) und Rundgang für Chips und Köpfe — einmal je Seite.
  useEffect(() => {
    void loadCatalog()
  }, [loadCatalog])

  useEffect(() => {
    // Auch wenn die Hör-Blase erscheint: Sie steht am Ende, wie eine Nachricht.
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, micOpen])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const text = draft.trim()
    if (!text || isBusy) return
    setDraft('')
    void sendMessage(text)
  }

  const showStatus = avatarState !== 'idle'

  /** „Neues Gespräch“ aus dem Kopf: mit Rückfrage, sobald etwas im Chat steht. */
  function neuAnfangen(mitRueckfrage: boolean) {
    if (mitRueckfrage && messages.length > 0 && !window.confirm(t.newConversationConfirm)) return
    endVoice()
    void neuesGespraech()
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* First tab stop, ahead of the header controls, so keyboard and
          screen-reader users can jump straight to the input. */}
      <a
        href="#chat-input"
        className="sr-only-focusable absolute left-2 top-2 z-50 rounded-full bg-bg-elevated px-4 py-2"
      >
        {t.skipToInput}
      </a>
      <header className="glass sticky top-0 z-20 flex items-center gap-3 px-4 py-3">
        <Avatar state={avatarState} size={40} micLevel={micLevel} showLabel={false} />
        <div className="min-w-0 flex-1">
          <h1 className="text-lg leading-tight">K4ia</h1>
          {/* The state lives here instead of under the avatar: one line, and it
              only changes when something happens. */}
          <p className="truncate text-sm text-text-muted" aria-live="polite">
            {showStatus ? stateLabel(avatarState, t) : t.subtitle}
          </p>
        </div>
        {cameraConsent && (
          <>
            <input
              ref={fotoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => void handleFoto(e)}
            />
            <button
              type="button"
              onClick={() => fotoInputRef.current?.click()}
              disabled={fotoLaeuft}
              className="rounded-full bg-surface-2 px-4 py-2 text-sm font-semibold text-text disabled:opacity-60"
            >
              {fotoLaeuft ? t.looking : t.photo}
            </button>
          </>
        )}
        {cameraConsent && (
          <button
            onClick={() => setCameraOpen((v) => !v)}
            aria-pressed={cameraOpen}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${
              cameraOpen ? 'bg-status-active text-white' : 'bg-surface-2 text-text'
            }`}
          >
            {cameraOpen ? t.cameraOn : t.camera}
          </button>
        )}
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => neuAnfangen(true)}
            disabled={isBusy}
            className="rounded-full bg-surface-2 px-4 py-2 text-sm font-semibold text-text disabled:opacity-60"
          >
            {t.newConversation}
          </button>
        )}
        <ThemeToggle />
      </header>

      {/* Beim Wiederkommen (Flo, 22.10.): Gespräch von vorhin — weitermachen
          oder neu anfangen? Oder: Das alte war abgelaufen, hier ist ein neues. */}
      {sitzungsHinweis && (
        <div
          role="status"
          data-testid="sitzungs-hinweis"
          data-art={sitzungsHinweis.art}
          className="flex flex-wrap items-center gap-2 border-b border-border bg-bg-elevated px-4 py-2 text-sm"
        >
          <span className="min-w-0 flex-1">
            {(sitzungsHinweis.art === 'fortgesetzt' ? t.resumeHint : t.resetHint).replace(
              '{wann}',
              wann(sitzungsHinweis.am, locale),
            )}
          </span>
          {sitzungsHinweis.art === 'fortgesetzt' ? (
            <>
              <button
                type="button"
                onClick={hinweisSchliessen}
                className="rounded-full border border-border bg-bg-elevated px-3 py-1 text-sm font-semibold"
              >
                {t.resumeContinue}
              </button>
              <button
                type="button"
                onClick={() => neuAnfangen(false)}
                className="btn-primary rounded-full px-3 py-1 text-sm font-semibold"
              >
                {t.resumeNew}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={hinweisSchliessen}
              className="rounded-full border border-border bg-bg-elevated px-3 py-1 text-sm font-semibold"
            >
              {t.dismiss}
            </button>
          )}
        </div>
      )}

      {cameraOpen && cameraConsent && !voiceOpen && (
        <CameraView onClose={() => setCameraOpen(false)} />
      )}

      <div
        ref={listRef}
        className="flex-1 space-y-4 overflow-y-auto px-4 pb-6 pt-4"
        role="log"
        aria-label="Gesprächsverlauf"
      >
        {messages.length === 0 && (
          <div className="mx-auto mt-8 flex max-w-md flex-col items-center text-center">
            <Avatar state={avatarState} size={112} micLevel={micLevel} showLabel={false} />
            <h2 className="mt-6 text-3xl">{t.greetingTitle}</h2>
            <p className="mt-3 text-text-muted">{t.greetingText}</p>
            <div className="mt-8 grid w-full gap-3">
              {starters(t).map((s) => (
                <button
                  key={s.question}
                  type="button"
                  onClick={() => void pickSuggestion(s.question)}
                  className="card flex items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-2"
                >
                  <span className="w-12 shrink-0 font-mono text-xs uppercase tracking-wider text-brand">
                    {s.label}
                  </span>
                  <span className="flex-1">{s.question}</span>
                </button>
              ))}
            </div>
            {/* Versionsstand, klein und nur auf dem leeren Start: Wer testet,
                sieht so, ob der letzte Push schon läuft — Besucher übersehen es.
                Technische Marke, kein Inhalt: Vorleser überspringen sie, und die
                Kontrastprüfung lässt sie aus (e2e/axe.ts, WCAG 1.4.3 „incidental“). */}
            <p
              aria-hidden="true"
              data-versionsstand
              className="mt-10 select-none font-mono text-[9px] leading-none text-text-muted opacity-40"
            >
              {__K4IA_VERSION__}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        <ListeningBubble />
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="mx-4 mb-3 rounded-2xl bg-surface px-4 py-3 text-sm shadow-float"
        >
          <span className="font-semibold text-status-error">{t.errorPrefix} </span>
          {errorMessage} {t.errorSuffix}
        </div>
      )}

      <div className="px-4 pb-3">
        <SuggestionChips suggestions={suggestions} onPick={(text) => void pickSuggestion(text)} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="glass mx-4 mb-3 flex items-center gap-2 rounded-full p-1.5 pl-5 shadow-float"
      >
        <label htmlFor="chat-input" className="sr-only">
          {t.yourMessage}
        </label>
        <input
          id="chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t.placeholder}
          className="min-w-0 flex-1 bg-transparent py-2.5 text-base outline-none placeholder:text-text-muted"
          autoComplete="off"
        />
        {draft.trim() || !micConsent ? (
          <button
            type="submit"
            aria-label={t.send}
            disabled={!draft.trim() || isBusy}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent transition disabled:cursor-not-allowed disabled:opacity-30"
          >
            <SendIcon />
          </button>
        ) : (
          <button
            type="button"
            onClick={startVoice}
            aria-label={t.voiceModeOpen}
            title={t.voiceModeOpen}
            className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-text pl-3.5 pr-4 text-sm font-semibold text-bg transition"
          >
            <WaveIcon />
            <span className="hidden min-[380px]:inline">{t.voiceModeTalk}</span>
          </button>
        )}
      </form>

      {!micConsent && (
        <p className="px-6 pb-3 text-center text-[11px] text-text-muted">{t.voiceNoMic}</p>
      )}
      <div className="pb-[env(safe-area-inset-bottom)]" />

      {voiceOpen && (
        <VoiceModeOverlay
          status={voice.status}
          paused={voice.paused}
          errorMessage={voice.errorMessage}
          onEnd={endVoice}
          onPause={voice.pause}
          onResume={voice.zuhoeren}
          cameraOpen={cameraOpen && cameraConsent}
          onToggleCamera={cameraConsent ? () => setCameraOpen((v) => !v) : null}
          bargeIn={voice.dazwischenAn}
        />
      )}
    </div>
  )
}

/** Schallwellen wie beim Sprachmodus von ChatGPT — erkennbar ohne Wort. */
function WaveIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 19V5M5 12l7-7 7 7"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** „von 14:05 Uhr“ am selben Tag, sonst „vom 18.10., 14:05“ — in der Sprache des Besuchers. */
function wann(am: string | null, locale: 'de' | 'en'): string {
  if (!am) return locale === 'de' ? 'vorhin' : 'earlier'
  const d = new Date(am)
  if (Number.isNaN(d.getTime())) return locale === 'de' ? 'vorhin' : 'earlier'
  const heute = new Date().toDateString() === d.toDateString()
  const tag = locale === 'de' ? 'de-DE' : 'en-GB'
  const zeit = d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' })
  if (heute) return locale === 'de' ? `${zeit} Uhr` : zeit
  const datum = d.toLocaleDateString(tag, { day: '2-digit', month: '2-digit' })
  return `${datum}, ${zeit}`
}
